// ============================================================================
// VoiceRoomsScreen.tsx — приложение «Голосовые комнаты» (волна 2, D7).
//
// Список постоянных комнат (публичные/мои), создание, вход. Внутри комнаты —
// VoiceRoomView (участники/роли/микрофоны + LiveKit-аудио). SSoT — сервер
// (VoiceRoomService); live-обновления — события шины voice.room.*.{roomId}.
// Долгих таймеров-поллингов НЕТ: список перечитывается только по событиям и
// действиям пользователя (небольшая задержка-склейка пачки событий).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Globe, Lock, LogIn, Pencil, Plus, RefreshCw, Search, Trash2, Users } from 'lucide-react';
import type { VoiceRoom } from '@zdc/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { confirmDialog } from '@/lib/dialogs';
import { errorCode, friendlyError, logErrorToJournal } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { useSecurityAccess } from '@/lib/securityRoles';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { CreateVoiceRoomDialog } from './CreateVoiceRoomDialog';
import { VoiceRoomEditDialog } from './VoiceRoomEditDialog';
import { VoiceRoomView } from './VoiceRoomView';
import { readMutedPref } from './prefs';

const CODE_PERMISSION_DENIED = 7;

function isPermissionDenied(e: unknown): boolean {
  const code = errorCode(e);
  if (typeof code === 'number') return code === CODE_PERMISSION_DENIED;
  if (typeof code === 'string') {
    return code.trim().toLowerCase().replace(/[\s_-]/g, '') === 'permissiondenied';
  }
  return false;
}

export function VoiceRoomsScreen() {
  const myId = String(useAuthStore((s) => s.user?.id) ?? '');
  const access = useSecurityAccess();
  const [rooms, setRooms] = useState<VoiceRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [enterError, setEnterError] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRoom, setEditRoom] = useState<VoiceRoom | null>(null);
  const [insideId, setInsideId] = useState('');
  const [enterBusy, setEnterBusy] = useState('');
  const [deleteBusy, setDeleteBusy] = useState('');
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    try {
      const r = await getSDK().voiceRooms.listRooms({ onlyMine });
      setRooms([...r.items]);
      setLoadError('');
    } catch (e) {
      logErrorToJournal(e, 'Голосовые комнаты: список комнат');
      if (!silent) setLoadError(friendlyError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [onlyMine]);

  useEffect(() => {
    void load(false);
  }, [load]);

  // Live: любое событие комнат → мягкое перечитывание списка (склейка 250 мс).
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('voice.room.')) return;
      if (reloadTimer.current !== null) clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => {
        reloadTimer.current = null;
        void load(true);
      }, 250);
    });
    return () => {
      unsub();
      if (reloadTimer.current !== null) {
        clearTimeout(reloadTimer.current);
        reloadTimer.current = null;
      }
    };
  }, [load]);

  const enter = useCallback(async (roomId: string) => {
    const roomName = rooms.find((r) => r.id === roomId)?.name || '';
    setEnterBusy(roomId);
    setEnterError('');
    try {
      await getSDK().voiceRooms.joinRoom({ roomId, mutedSelf: readMutedPref() });
      setInsideId(roomId);
    } catch (e) {
      logErrorToJournal(e, 'Голосовые комнаты: вход в комнату');
      const suffix = roomName ? ` «${roomName}»` : '';
      setEnterError(
        isPermissionDenied(e)
          ? `Нет доступа к комнате${suffix} — нужна роль из настроек комнаты`
          : friendlyError(e),
      );
      // Комната могла быть удалена между показом списка и входом — обновляем.
      void load(true);
    } finally {
      setEnterBusy('');
    }
  }, [load, rooms]);

  const exitRoom = useCallback(() => {
    setInsideId('');
    void load(true);
  }, [load]);

  // Переименование: сервер возвращает комнату → заменяем в списке без
  // полного перечитывания (live-событие всё равно догонит остальных).
  const applyUpdated = useCallback((updated: VoiceRoom) => {
    setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const removeRoom = useCallback(async (room: VoiceRoom) => {
    const ok = await confirmDialog(
      `Удалить комнату «${room.name}»? Участники будут отключены, отменить это нельзя.`,
      'Удаление комнаты',
    );
    if (!ok) return;
    setDeleteBusy(room.id);
    setEnterError('');
    try {
      await getSDK().voiceRooms.deleteRoom({ roomId: room.id });
      setRooms((prev) => prev.filter((r) => r.id !== room.id));
      useToastStore.getState().showSuccess('Комната удалена');
    } catch (e: unknown) {
      logErrorToJournal(e, 'Голосовые комнаты: удаление комнаты');
      setEnterError(
        isPermissionDenied(e)
          ? `Нет прав на удаление комнаты «${room.name}» — это может только владелец или администратор`
          : friendlyError(e),
      );
    } finally {
      setDeleteBusy('');
    }
  }, []);

  // Внутри комнаты — отдельный экран (свои подписки и медиа).
  if (insideId) {
    return <VoiceRoomView roomId={insideId} onExit={exitRoom} />;
  }

  const q = query.trim().toLowerCase();
  const visible = rooms.filter((r) => {
    if (!q) return true;
    return `${r.name} ${r.topic} ${r.creatorName}`.toLowerCase().includes(q);
  });

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="shrink-0 border-b px-3 py-2 space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Голосовые комнаты</div>
            <div className="text-xs text-muted-foreground">
              Постоянные комнаты: вошёл-вышел, видно кто внутри. Разговор — через медиа.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load(false)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Обновить
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Создать
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border overflow-hidden">
            <button
              type="button"
              onClick={() => setOnlyMine(false)}
              className={`px-2.5 py-1 text-xs ${onlyMine ? 'bg-background' : 'bg-primary text-primary-foreground'}`}
            >
              Все комнаты
            </button>
            <button
              type="button"
              onClick={() => setOnlyMine(true)}
              className={`px-2.5 py-1 text-xs ${onlyMine ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
            >
              Мои комнаты
            </button>
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по имени, теме, создателю…"
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>
        {enterError && <div className="text-xs text-destructive">{enterError}</div>}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {loading && rooms.length === 0 ? (
          <div className="text-xs text-muted-foreground">Загрузка комнат…</div>
        ) : loadError ? (
          <div className="space-y-2">
            <div className="text-xs text-destructive">{loadError}</div>
            <Button variant="outline" size="sm" onClick={() => void load(false)}>
              Повторить
            </Button>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {rooms.length === 0
              ? onlyMine
                ? 'У вас пока нет комнат — создайте первую.'
                : 'Комнат пока нет — создайте первую.'
              : 'Ничего не найдено по поиску.'}
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map((room) => {
              const isMine = room.createdBy === myId;
              const isPrivate = room.settings?.isPublic === false;
              const canManageRoom = isMine || access.isAdmin;
              return (
                <div
                  key={room.id}
                  className="flex flex-col gap-2 rounded-lg border bg-card p-3 text-card-foreground"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" title={room.name}>
                        {room.name}
                        {isMine && (
                          <span className="ml-1.5 rounded bg-primary/10 px-1 py-0.5 text-[10px] text-primary align-middle">
                            моя
                          </span>
                        )}
                      </div>
                      {room.topic && (
                        <div className="text-xs text-muted-foreground line-clamp-2">{room.topic}</div>
                      )}
                    </div>
                    <span className="flex items-center gap-0.5 shrink-0">
                      {isPrivate ? (
                        <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Приватная комната" />
                      ) : (
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" aria-label="Публичная комната" />
                      )}
                      {/* Права владельца: переименование и удаление. Модератор
                          комнаты правит её внутри (VoiceRoomView); список даёт
                          действия только владельцу/администратору. */}
                      {canManageRoom && (
                        <>
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            title="Переименовать комнату"
                            aria-label="Переименовать комнату"
                            onClick={() => setEditRoom(room)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                            title="Удалить комнату"
                            aria-label="Удалить комнату"
                            disabled={deleteBusy === room.id}
                            onClick={() => void removeRoom(room)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" /> {room.activeCount} в комнате
                    </span>
                    <span className="truncate">
                      {isPrivate ? 'приватная' : 'публичная'}
                      {room.creatorName ? ` · ${room.creatorName}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-muted-foreground">
                      {isPrivate && room.settings && room.settings.roleIds.length > 0
                        ? `роли: ${room.settings.roleIds.join(', ')}`
                        : ''}
                    </span>
                    <Button
                      size="sm"
                      variant={room.activeCount > 0 ? 'default' : 'outline'}
                      onClick={() => void enter(room.id)}
                      disabled={enterBusy !== ''}
                    >
                      <LogIn className="h-4 w-4 mr-1" />
                      {enterBusy === room.id ? 'Входим…' : 'Войти'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <VoiceRoomEditDialog
        open={editRoom !== null}
        room={editRoom}
        onClose={() => setEditRoom(null)}
        onUpdated={applyUpdated}
      />

      <CreateVoiceRoomDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(roomId) => {
          setCreateOpen(false);
          void enter(roomId);
        }}
      />
    </div>
  );
}
